import { useQuery } from "@tanstack/react-query";
import { apiGet } from "../../lib/api";
import { cn } from "../../lib/cn";

type WaitlistEntry = {
  id: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  service_name?: string | null;
  desired_day: string;
  status: string;
  notes?: string | null;
  contacted_at?: string | null;
  created_at: string;
};

type WaitlistData = { items: WaitlistEntry[]; meta: { total: number } };

const STATUS_COLORS: Record<string, string> = {
  open: "bg-yellow-100 text-yellow-800",
  contacted: "bg-blue-100 text-blue-800",
  booked: "bg-green-100 text-green-800",
  closed: "bg-gray-100 text-gray-500",
};

export function TgWaitlistPage() {
  const { data, isLoading } = useQuery<WaitlistData>({
    queryKey: ["tg-waitlist"],
    queryFn: () => apiGet("/v3/tg/waitlist"),
  });

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-gray-900">Waitlist</h1>
        {data && <span className="text-sm text-gray-500">{data.meta.total} entries</span>}
      </div>

      {isLoading && <div className="text-gray-400 text-center py-6">Loading…</div>}

      {!isLoading && (data?.items ?? []).length === 0 && (
        <div className="text-gray-400 text-center py-12">No open waitlist entries.</div>
      )}

      <div className="space-y-2">
        {(data?.items ?? []).map((entry) => (
          <div key={entry.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 space-y-1">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold text-gray-900">{entry.customer_name ?? "Unknown"}</p>
                <p className="text-sm text-gray-500">{entry.service_name}</p>
                <p className="text-sm text-gray-600">Desired: {entry.desired_day}</p>
              </div>
              <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", STATUS_COLORS[entry.status] ?? "bg-gray-100 text-gray-600")}>
                {entry.status}
              </span>
            </div>
            {entry.customer_phone && (
              <a
                href={`tel:${entry.customer_phone}`}
                className="block text-sm text-teal-600 underline"
              >
                {entry.customer_phone}
              </a>
            )}
            {entry.notes && (
              <p className="text-xs text-gray-500 italic">{entry.notes}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
